import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../../../api/agent";
import { AppFilter, ProductFilter } from "../../../../models/app/AppModels";
import { AppResponse } from "../../../../models/app/AppResponse";
import {
  CatalogCategoryResponse,
  CatalogManufacturerResponse,
  Category,
  Image,
  Product,
  ProductManufacturer,
  ProductSection,
  ProductState,
} from "../../../../models/features/Product/Product";
import { DmsWhlegoodRequestDto } from "../../../../models/features/ShoppingPortal/DmsPart";

const initialState: ProductState = {
  filters: {
    quickFilterValues: [],
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
  },
};

export const productSlice = createSlice({
  name: "product",
  initialState: initialState,
  reducers: {
    removeFilters: (state) => {
      state.filters.quickFilterValues = [];
      state.filters.items = [];
      state.filters.pageIndex = 0;
      state.filters.pageSize = 10;
      state.filters.totalRecords = 0;
    },
    saveFilters: (state, action: PayloadAction<AppFilter>) => {
      state.filters = action.payload;
    },
  },
});

export const productApi = createApi({
  reducerPath: "productApi",
  baseQuery: axiosBaseQuery(),

  endpoints: (build) => ({
    getProducts: build.mutation<any, { paging: ProductFilter }>({
      query: ({ paging }) => ({
        url: `api/GetProducts`,
        method: "post",
        data: paging,
      }),
    }),
    getTaxanomyCategories: build.mutation<any, { searchQuery: string }>({
      query: ({ searchQuery }) => ({
        url: `api/Shopify/GetProductTaxanomyCategories?searchQuery=${searchQuery}`,
        method: "post",
        data: null,
      }),
    }),
    getCategories: build.query<AppResponse<Category>, void>({
      query: () => ({
        url: `api/GetCategories`,
      }),
    }),
    getCategorListWithProductCount: build.query<
      AppResponse<CatalogCategoryResponse>,
      void
    >({
      query: () => ({
        url: `api/Category/GetCategoryListWithProductCount`,
      }),
    }),
    getManufacturers: build.query<AppResponse<ProductManufacturer>, void>({
      query: () => ({
        url: `api/GetManufacturers`,
      }),
    }),
    getCatalogMfrsWithProductCount: build.query<
      AppResponse<CatalogManufacturerResponse>,
      void
    >({
      query: () => ({
        url: `api/Manufacturer/GetCatalogMfrsWithProductsCount`,
      }),
    }),
    createProduct: build.mutation<any, { data: any }>({
      query: ({ data }) => ({
        url: `api/Product/CreateCatalogProduct`,
        method: "post",
        data: data,
      }),
    }),
    addCategory: build.mutation<AppResponse<Category>, { data: Category }>({
      query({ data }) {
        return {
          url: `api/AddCategory`,
          method: "post",
          data: data,
        };
      },
    }),
    addManufacturer: build.mutation<
      AppResponse<ProductManufacturer>,
      { data: ProductManufacturer }
    >({
      query({ data }) {
        return {
          url: `api/AddManufacturer`,
          method: "post",
          data: data,
        };
      },
    }),
    editCategory: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/EditCategory`,
          method: "post",
          data: data,
        };
      },
    }),
    editManufacturer: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/EditManufacturer`,
          method: "post",
          data: data,
        };
      },
    }),
    deleteProduct: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/DeleteProduct`,
          method: "delete",
          data: data,
        };
      },
    }),
    deleteCategory: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/DeleteCategory`,
          method: "delete",
          data: data,
        };
      },
    }),
    deleteManufacturer: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/DeleteManufacturer`,
          method: "delete",
          data: data,
        };
      },
    }),
    deleteProductImage: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/DeleteProductImage`,
          method: "delete",
          data: data,
        };
      },
    }),
    getProductById: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/GetProductById`,
          method: "post",
          data: data,
        };
      },
    }),
    addProductSection: build.mutation<
      AppResponse<ProductSection>,
      { data: any }
    >({
      query({ data }) {
        return {
          url: `api/Product/AddProductSection`,
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data; ",
          },
          data: data,
        };
      },
    }),
    updateProductSection: build.mutation<
      AppResponse<ProductSection>,
      { data: any }
    >({
      query({ data }) {
        return {
          url: `api/Product/UpdateProductSection`,
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data; ",
          },
          data: data,
        };
      },
    }),
    GetProductSection: build.mutation<any, { productId: any }>({
      query({ productId }) {
        return {
          url: `api/Product/GetProductSection?productId=${productId}`,
          method: "post",
          data: productId,
        };
      },
    }),
    deleteProductSection: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Product/DeleteProductSection`,
          method: "delete",
          data: data,
        };
      },
    }),

    GetProductSectionById: build.mutation<
      AppResponse<ProductSection>,
      { data: any }
    >({
      query({ data }) {
        return {
          url: `api/Product/GetProductSectionById`,
          method: "post",
          data: data,
        };
      },
    }),

    addProductSectionItem: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Product/AddProductSectionItem`,
          method: "post",
          data: data,
        };
      },
    }),
    updateProductSectionItem: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Product/UpdateProductSectionItem`,
          method: "post",
          data: data,
        };
      },
    }),
    GetProductSectionItems: build.query<any, { productSectionId: any }>({
      query({ productSectionId }) {
        return {
          url: `api/Product/GetProductSectionItems?productSectionId=${productSectionId}`,
        };
      },
    }),
    deleteProductSectionItem: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Product/DeleteProductSectionItem`,
          method: "delete",
          data: data,
        };
      },
    }),
    getProductWithSections: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Product/GetProductWithSections`,
          method: "post",
          data: data,
        };
      },
    }),
    GetProductSectionParts: build.mutation<any, { sectionId: any }>({
      query({ sectionId }) {
        return {
          url: `api/Product/GetProductSectionParts?sectionId=${sectionId}`,
          method: "post",
          data: sectionId,
        };
      },
    }),
    SyncNewArrivalProduct: build.mutation<any, { productId: any }>({
      query({ productId }) {
        return {
          url: `api/Shopify/SyncNewArrivalProduct?productId=${productId}`,
          method: "post",
          data: productId,
        };
      },
    }),
    getNewArrivalProducts: build.mutation<any, { paging: ProductFilter }>({
      query: ({ paging }) => ({
        url: `api/Shopify/GetSyncedNewArrivalProducts`,
        method: "post",
        data: paging,
      }),
    }),
    removeAggregateProductAsync: build.mutation<any, { aggregateId: any }>({
      query({ aggregateId }) {
        return {
          url: `api/Shopify/RemoveAggregateProductAsync?aggregateId=${aggregateId}`,
          method: "post",
          data: aggregateId,
        };
      },
    }),
    getUsedProducts: build.mutation<any, { paging: DmsWhlegoodRequestDto }>({
      query: ({ paging }) => ({
        url: `api/Shopify/GetUsedProducts`,
        method: "post",
        data: paging,
      }),
    }),
    syncUsedProduct: build.mutation<any, { request: any }>({
      query: ({ request }) => ({
        url: `api/Shopify/AddUsedProduct`,
        method: "post",
        data: request,
      }),
    }),
    addUsedProductImage: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Shopify/AddUsedProductImage`,
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data; ",
          },
          data: data,
        };
      },
    }),
    getShopifyProductImages: build.mutation<any, { productId: any }>({
      query({ productId }) {
        return {
          url: `api/Shopify/GetShopifyProductImages?shopifyProductId=${productId}`,
          method: "post",
          data: productId,
        };
      },
    }),
    deleteShopifyProductImages: build.mutation<
      any,
      { aggregateId?: string; shopifyProductId: string; shopifyImageId: string }
    >({
      query(data) {
        return {
          url: `api/Shopify/DeleteShopifyProductImages`,
          method: "delete",
          data: data,
        };
      },
    }),
    addManufacturerImage: build.mutation<
      any,
      { data: any; manufacturerId: string }
    >({
      query({ data, manufacturerId }) {
        return {
          url: `api/Manufacturer/AddManufacturerImage?manufacturerId=${manufacturerId}`,
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data; ",
          },
          data: data,
        };
      },
    }),
    migrateProducts: build.mutation<
      any,
      { sourceId: string; targetId: string }
    >({
      query(data) {
        return {
          url: `api/Category/MigrateProducts`,
          method: "post",
          data: data,
        };
      },
    }),

    setImageSequence: build.mutation<any, { imageIds: string[] }>({
      query(data) {
        return {
          url: `api/Product/SetImageSequence`,
          method: "post",
          data: data,
        };
      },
    }),
    RemoveBulkAggregateProducts: build.mutation<
      any,
      { ProductAggregateIds: any[] }
    >({
      query(data) {
        return {
          url: `api/Shopify/RemoveBulkAggregateProducts`,
          method: "post",
          data: data,
        };
      },
    }),
    getStoreProductDescription: build.mutation<any, { productId: any }>({
      query({ productId }) {
        return {
          url: `api/Store/GetStoreProductDescription?productId=${productId}`,
          method: "post",
          data: productId,
        };
      },
    }),
    updateStoreProductDetails: build.mutation<any, { data: any }>({
      query({ data }) {
        return {
          url: `api/Store/UpdateStoreProductDetails`,
          method: "post",
          data: data,
        };
      },
    }),
    revertShopifyCustomDetails: build.mutation<any, { aggregateId: string }>({
      query({ aggregateId }) {
        return {
          url: `api/Shopify/RevertShopifyCustomDetails?aggregateId=${aggregateId}`,
          method: "post",
          data: aggregateId,
        };
      },
    }),
    addCustomPrice: build.mutation<
      any,
      {
        aggregateId: string;
        method: string;
        amount: number;
        applyOn: string;
      }
    >({
      query: (data) => ({
        url: `api/Shopify/AddCustomPrice`,
        method: "post",
        data: data,
      }),
    }),
  }),
});

export const {
  useGetProductsMutation,
  useGetCategoriesQuery,
  useGetManufacturersQuery,
  useDeleteProductMutation,
  useAddCategoryMutation,
  useDeleteCategoryMutation,
  useEditCategoryMutation,
  useAddManufacturerMutation,
  useEditManufacturerMutation,
  useDeleteManufacturerMutation,
  useDeleteProductImageMutation,
  useGetProductByIdMutation,
  useAddProductSectionMutation,
  useGetProductSectionMutation,
  useDeleteProductSectionMutation,
  useGetProductSectionByIdMutation,
  useAddProductSectionItemMutation,
  useDeleteProductSectionItemMutation,
  useUpdateProductSectionItemMutation,
  useGetProductWithSectionsMutation,
  useGetProductSectionItemsQuery,
  useGetProductSectionPartsMutation,
  useUpdateProductSectionMutation,
  useSyncNewArrivalProductMutation,
  useGetNewArrivalProductsMutation,
  useRemoveAggregateProductAsyncMutation,
  useGetUsedProductsMutation,
  useSyncUsedProductMutation,
  useAddUsedProductImageMutation,
  useGetShopifyProductImagesMutation,
  useDeleteShopifyProductImagesMutation,
  useGetCatalogMfrsWithProductCountQuery,
  useAddManufacturerImageMutation,
  useGetTaxanomyCategoriesMutation,
  useMigrateProductsMutation,
  useGetCategorListWithProductCountQuery,
  useCreateProductMutation,
  useSetImageSequenceMutation,
  useRemoveBulkAggregateProductsMutation,
  useGetStoreProductDescriptionMutation,
  useUpdateStoreProductDetailsMutation,
  useRevertShopifyCustomDetailsMutation,
  useAddCustomPriceMutation,
} = productApi;

export const { saveFilters, removeFilters } = productSlice.actions;
export const productReducer = productSlice.reducer;
